import { supabase } from '../lib/supabase';
import { isDemoMode, getDemoOrgId } from '../utils/demoHelper';

function isSchemaError(error) {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
}

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) return obj?.map(item => toCamelCase(item));
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) return obj?.map(item => toSnakeCase(item));
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// Get all team members in the same organization
export const getTeamMembers = async () => {
  try {
    // Demo mode: use stored demo org_id directly (no real Supabase session)
    if (isDemoMode()) {
      const demoOrgId = getDemoOrgId();
      let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });
      if (demoOrgId) {
        query = query?.eq('organization_id', demoOrgId);
      }
      const { data, error } = await query;
      if (error) {
        console.log('Get team members error (demo):', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    }

    // Use getSession() (local, no network call) instead of getUser() (network call)
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session?.user) return [];
    const user = session?.user;

    // Get current user's org
    const { data: myProfile } = await supabase
      ?.from('user_profiles')
      ?.select('organization_id')
      ?.eq('id', user?.id)
      ?.maybeSingle();

    let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

    if (myProfile?.organization_id) {
      query = query?.eq('organization_id', myProfile?.organization_id);
    } else {
      // No org yet — just return the current user's own profile
      query = query?.eq('id', user?.id);
    }

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Get team members error:', error?.message);
      // Fallback: return just the current user's profile
      const { data: selfProfile } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', user?.id)
        ?.maybeSingle();
      return selfProfile ? toCamelCase([selfProfile]) : [];
    }

    const members = toCamelCase(data || []);

    // If no members found but we have a session, ensure current user is included
    if (members?.length === 0) {
      const { data: selfProfile } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', user?.id)
        ?.maybeSingle();
      return selfProfile ? toCamelCase([selfProfile]) : [];
    }

    // Also fetch pending invites for this org and merge them in
    if (myProfile?.organization_id) {
      const { data: pendingInvites } = await supabase
        ?.from('pending_invites')
        ?.select('*')
        ?.eq('organization_id', myProfile?.organization_id)
        ?.order('created_at', { ascending: false });

      if (pendingInvites?.length > 0) {
        // Build a set of emails already present in user_profiles to avoid duplicates.
        // When inviteUserByEmail() fires, Supabase's on_auth_user_created trigger
        // immediately inserts a user_profiles row, so the same person would appear
        // twice (once as active in user_profiles, once as pending in pending_invites).
        const existingEmails = new Set(members?.map(m => m?.email?.toLowerCase()));
        const pendingMembers = toCamelCase(pendingInvites)
          ?.filter(invite => !existingEmails?.has(invite?.email?.toLowerCase()))
          ?.map(invite => ({
            ...invite,
            isPendingInvite: true
          }));
        if (pendingMembers?.length > 0) {
          return [...members, ...pendingMembers];
        }
      }
    }

    return members;
  } catch (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
};

// Get team member by ID
export const getTeamMemberById = async (id) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', id)?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    return toCamelCase(data);
  } catch (error) {
    if (isSchemaError(error)) throw error;
    return null;
  }
};

// Get projects assigned to a team member
export const getTeamMemberProjects = async (memberId) => {
  try {
    const { data, error } = await supabase?.from('project_team_assignments')?.select('*, projects(id, customer_name, address, phase, priority)')?.eq('team_member_id', memberId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
};

// Update team member profile
export const updateTeamMember = async (id, updates) => {
  try {
    const snakeCaseUpdates = toSnakeCase(updates);
    snakeCaseUpdates.updated_at = new Date()?.toISOString();

    // First check if this is a pending invite record
    const { data: pendingCheck } = await supabase
      ?.from('pending_invites')
      ?.select('id')
      ?.eq('id', id)
      ?.maybeSingle();

    if (pendingCheck) {
      // Update pending_invites table instead
      const { data, error } = await supabase
        ?.from('pending_invites')
        ?.update(snakeCaseUpdates)
        ?.eq('id', id)
        ?.select()
        ?.maybeSingle();
      if (error) {
        if (isSchemaError(error)) throw error;
        throw error;
      }
      if (!data) throw new Error('Update failed: record not found or permission denied.');
      return toCamelCase({ ...data, isPendingInvite: true });
    }

    const { data, error } = await supabase?.from('user_profiles')?.update(snakeCaseUpdates)?.eq('id', id)?.select()?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) throw error;
      throw error;
    }
    if (!data) {
      throw new Error('Update failed: record not found or permission denied.');
    }
    return toCamelCase(data);
  } catch (error) {
    throw error;
  }
};

// Update member status only
export const updateMemberStatus = async (id, status) => {
  try {
    // Check if this is a pending invite record
    const { data: pendingCheck } = await supabase
      ?.from('pending_invites')
      ?.select('id')
      ?.eq('id', id)
      ?.maybeSingle();

    if (pendingCheck) {
      const { data, error } = await supabase
        ?.from('pending_invites')
        ?.update({ status, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.maybeSingle();
      if (error) {
        if (isSchemaError(error)) throw error;
        throw error;
      }
      if (!data) throw new Error('Status update failed: record not found or permission denied.');
      return toCamelCase({ ...data, isPendingInvite: true });
    }

    const { data, error } = await supabase?.from('user_profiles')?.update({ status, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) throw error;
      throw error;
    }
    if (!data) {
      throw new Error('Status update failed: record not found or permission denied.');
    }
    return toCamelCase(data);
  } catch (error) {
    throw error;
  }
};

// Update member role
export const updateMemberRole = async (id, role) => {
  try {
    // Check if this is a pending invite record
    const { data: pendingCheck } = await supabase
      ?.from('pending_invites')
      ?.select('id')
      ?.eq('id', id)
      ?.maybeSingle();

    if (pendingCheck) {
      const { data, error } = await supabase
        ?.from('pending_invites')
        ?.update({ role, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.maybeSingle();
      if (error) {
        if (isSchemaError(error)) throw error;
        throw error;
      }
      if (!data) throw new Error('Role update failed: record not found or permission denied.');
      return toCamelCase({ ...data, isPendingInvite: true });
    }

    const { data, error } = await supabase?.from('user_profiles')?.update({ role, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) throw error;
      throw error;
    }
    if (!data) {
      throw new Error('Role update failed: record not found or permission denied.');
    }
    return toCamelCase(data);
  } catch (error) {
    throw error;
  }
};

// Delete a team member permanently
export const deleteTeamMember = async (id, isPendingInvite = false) => {
  try {
    if (isPendingInvite) {
      // Delete from pending_invites only
      const { error } = await supabase
        ?.from('pending_invites')
        ?.delete()
        ?.eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        throw error;
      }
      return true;
    }

    // Remove from project_team_assignments first
    await supabase
      ?.from('project_team_assignments')
      ?.delete()
      ?.eq('team_member_id', id);

    // Delete from user_profiles
    const { error } = await supabase
      ?.from('user_profiles')
      ?.delete()
      ?.eq('id', id);

    if (error) {
      if (isSchemaError(error)) throw error;
      throw error;
    }
    return true;
  } catch (error) {
    throw error;
  }
};

// Invite a new team member (stores in pending_invites — no auth.users FK required)
export const inviteTeamMember = async (memberData) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session?.user) throw new Error('Not authenticated');
    const user = session?.user;

    const { data: myProfile } = await supabase
      ?.from('user_profiles')
      ?.select('organization_id')
      ?.eq('id', user?.id)
      ?.maybeSingle();

    // Check for duplicate email in pending_invites
    const { data: existingInvite } = await supabase
      ?.from('pending_invites')
      ?.select('id')
      ?.eq('email', memberData?.email)
      ?.maybeSingle();

    if (existingInvite) {
      throw new Error('An invitation has already been sent to this email address.');
    }

    // Check for duplicate email in user_profiles (already a member)
    const { data: existingMember } = await supabase
      ?.from('user_profiles')
      ?.select('id')
      ?.eq('email', memberData?.email)
      ?.maybeSingle();

    if (existingMember) {
      throw new Error('A team member with this email address already exists.');
    }

    const organizationId = myProfile?.organization_id || null;

    // Insert into pending_invites first (so we have the record before the email is sent)
    const { data: pendingRecord, error: pendingError } = await supabase
      ?.from('pending_invites')
      ?.insert({
        email: memberData?.email,
        full_name: memberData?.fullName,
        phone: memberData?.phone || null,
        department: memberData?.department || null,
        job_title: memberData?.jobTitle || null,
        role: memberData?.role || 'viewer',
        status: 'pending',
        organization_id: organizationId,
        invited_by: user?.id,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })
      ?.select()
      ?.single();

    if (pendingError) {
      if (isSchemaError(pendingError)) throw pendingError;
      throw pendingError;
    }

    // Call the invite-team-member edge function to send the official Supabase invite email
    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/invite-team-member`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify({
          email: memberData?.email,
          fullName: memberData?.fullName,
          role: memberData?.role || 'viewer',
          organizationId: organizationId,
          invitedBy: user?.id
        })
      });

      const result = await response?.json();

      if (!response?.ok) {
        // If the edge function fails, we still keep the pending_invites record
        // but surface the error to the user
        console.warn('Edge function invite error:', result?.error);
        // Don't throw — the pending record is created; admin can resend later
      }
    } catch (edgeErr) {
      // Edge function call failed (network error etc.) — log but don't block
      console.warn('Could not send invite email:', edgeErr?.message);
    }

    // Log the invite activity
    await logActivity('invite', 'team_member', pendingRecord?.id, {
      invited_email: memberData?.email,
      role: memberData?.role
    });

    return toCamelCase(pendingRecord);
  } catch (error) {
    throw error;
  }
};

// Resend invite email to a pending invite member
export const resendInvite = async (invite) => {
  const { data: { session } } = await supabase?.auth?.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const response = await fetch(
    `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/invite-team-member`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        email: invite?.email,
        fullName: invite?.fullName || invite?.full_name || '',
        role: invite?.role || 'viewer',
        organizationId: invite?.organizationId || invite?.organization_id || null,
        invitedBy: session?.user?.id
      })
    }
  );

  const result = await response?.json();
  if (!response?.ok) {
    if (response?.status === 409) {
      // User already has an auth account — return a soft warning instead of throwing
      return { success: false, alreadyExists: true, message: result?.error || 'This email already has an account. They can log in directly.' };
    }
    throw new Error(result?.error || 'Failed to resend invitation');
  }
  return result;
};

// Remove/deactivate a team member
export const removeTeamMember = async (id) => {
  try {
    // Set status to inactive rather than hard delete
    const { data, error } = await supabase?.from('user_profiles')?.update({ status: 'inactive', updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) throw error;
      throw error;
    }

    if (!data) {
      // Member may be a pending invite — try removing from pending_invites
      const { error: inviteError } = await supabase?.from('pending_invites')?.delete()?.eq('id', id);
      if (inviteError) throw inviteError;
    }

    await logActivity('delete', 'team_member', id, { action: 'deactivated' });
    return data ? toCamelCase(data) : { id };
  } catch (error) {
    throw error;
  }
};

// Get all permissions
export const getPermissions = async () => {
  try {
    const { data, error } = await supabase?.from('permissions')?.select('*')?.order('resource', { ascending: true });

    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
};

// Get role permissions
export const getRolePermissions = async (role) => {
  try {
    const { data, error } = await supabase?.from('role_permissions')?.select('*, permissions(*)')?.eq('role', role);

    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
};

// Get activity logs
export const getActivityLogs = async (filters = {}) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session?.user) return [];

    // Get current user's organization_id
    const { data: myProfile } = await supabase
      ?.from('user_profiles')
      ?.select('organization_id')
      ?.eq('id', session?.user?.id)
      ?.maybeSingle();

    if (!myProfile?.organization_id) return [];

    // Get all user IDs in the same organization
    const { data: orgUsers } = await supabase
      ?.from('user_profiles')
      ?.select('id')
      ?.eq('organization_id', myProfile?.organization_id);

    const orgUserIds = (orgUsers || [])?.map(u => u?.id);
    if (!orgUserIds?.length) return [];

    let query = supabase
      ?.from('activity_logs')
      ?.select('*, user_profiles(full_name, email, avatar_url)')
      ?.in('user_id', orgUserIds)
      ?.order('created_at', { ascending: false });

    if (filters?.userId) query = query?.eq('user_id', filters?.userId);
    if (filters?.action) query = query?.eq('action', filters?.action);
    if (filters?.resourceType) query = query?.eq('resource_type', filters?.resourceType);
    if (filters?.limit) query = query?.limit(filters?.limit);

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
};

// Log activity
export const logActivity = async (action, resourceType, resourceId = null, details = null) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return null;

    const { data, error } = await supabase?.from('activity_logs')?.insert({
        user_id: user?.id,
        action,
        resource_type: resourceType,
        resource_id: resourceId,
        details
      })?.select()?.single();

    if (error) {
      console.log('Log activity error:', error?.message);
      return null;
    }
    return data;
  } catch (error) {
    console.log('Log activity exception:', error?.message);
    return null;
  }
};